import { KnowledgeFileData } from '../interfaces/Knowledge';

const DB_NAME = 'EloraAgentFormDB';
const DB_VERSION = 1;
const STORE_NAME = 'knowledgeFiles';

// Scoped by form type + entity id so staged files from one form/entity
// (e.g. creating a call agent) can't leak into another (e.g. editing an
// unrelated knowledge base) via the shared IndexedDB store.
export const buildKnowledgeFileCacheKey = (
  scope: string,
  entityId: string | undefined
): string => `${scope}:${entityId ?? 'new'}`;

class IndexDBService {
  private db: IDBDatabase | null = null;

  async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
    });
  }

  private async getRecord(cacheKey: string): Promise<
    | {
        id: string;
        files?: KnowledgeFileData[];
        pendingDeletions?: string[];
      }
    | undefined
  > {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      const request = store.get(cacheKey);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Reads and writes the record inside a single readwrite transaction so the
  // read-modify-write is atomic: IndexedDB serializes readwrite transactions
  // against the same store, so a concurrent call (e.g. saveKnowledgeFiles and
  // savePendingDeletions firing from two effects in the same render) can't
  // interleave its own get/put in between this call's get and put, which
  // would otherwise let one call's write silently clobber the other's.
  private async updateRecord(
    cacheKey: string,
    updater: (
      existing:
        | { files?: KnowledgeFileData[]; pendingDeletions?: string[] }
        | undefined
    ) => { files: KnowledgeFileData[]; pendingDeletions: string[] }
  ): Promise<void> {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const getRequest = store.get(cacheKey);

      getRequest.onsuccess = () => {
        const next = updater(getRequest.result);
        const putRequest = store.put({ id: cacheKey, ...next });
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // pendingDeletions is optional so callers that only touch `files` (the
  // pre-existing call sites) don't have to know about it; when omitted, the
  // cache key's existing pendingDeletions (if any) are preserved as-is.
  async saveKnowledgeFiles(
    cacheKey: string,
    files: KnowledgeFileData[],
    pendingDeletions?: string[]
  ): Promise<void> {
    await this.updateRecord(cacheKey, existing => ({
      files,
      pendingDeletions: pendingDeletions ?? existing?.pendingDeletions ?? [],
    }));
  }

  async savePendingDeletions(
    cacheKey: string,
    pendingDeletions: string[]
  ): Promise<void> {
    await this.updateRecord(cacheKey, existing => ({
      files: existing?.files ?? [],
      pendingDeletions,
    }));
  }

  async getKnowledgeFiles(cacheKey: string): Promise<KnowledgeFileData[]> {
    const record = await this.getRecord(cacheKey);
    return record?.files || [];
  }

  async getPendingDeletions(cacheKey: string): Promise<string[]> {
    const record = await this.getRecord(cacheKey);
    return record?.pendingDeletions || [];
  }

  async clearKnowledgeFiles(cacheKey: string): Promise<void> {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const request = store.delete(cacheKey);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(DB_NAME);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const indexDBService = new IndexDBService();
